"use client";

import { faUserPlus, faUserTie, faUserUnlock } from "@fortawesome/pro-solid-svg-icons";
import { Button, Dialog } from "@tosize/ui";
import { Slot } from "radix-ui";
import { type Dispatch, type ReactNode, type SetStateAction, useState } from "react";
import { match } from "ts-pattern";

export interface CombinedAccountFormClientProps {
	children: ReactNode;
	login: {
		title: string;
		description?: ReactNode;
		form: ReactNode;
	};
	create: {
		title: string;
		description?: ReactNode;
		form: ReactNode;
	};
	createBusiness: {
		title: string;
		description?: ReactNode;
		form: ReactNode;
	};
	actions: {
		login: string;
		create: string;
		createBusiness: string;
	};
	variant?: "default" | "light";
}

export function CombinedAccountFormClient(props: CombinedAccountFormClientProps) {
	const { children, login, create, createBusiness, actions, variant = "default" } = props;
	const [mode, setMode] = useState<"login" | "create" | "createBusiness">("login");
	const [open, setOpen] = useState(false);

	return (
		<>
			<Slot.Root
				onClick={(e) => {
					e.stopPropagation();
					setOpen(!open);
				}}
			>
				{children}
			</Slot.Root>

			<Dialog
				open={open}
				onOpenChange={() => {
					setOpen(!open);
					setMode("login");
				}}
				title={match(mode)
					.with("create", () => create.title)
					.with("createBusiness", () => createBusiness.title)
					.otherwise(() => login.title)}
				subtitle={match(mode)
					.with("create", () => create.description)
					.with("createBusiness", () => createBusiness.description)
					.otherwise(() => login.description)}
			>
				<CombinedAccountFormContentClient
					login={login}
					create={create}
					createBusiness={createBusiness}
					actions={actions}
					variant={variant}
					mode={mode}
					setMode={setMode}
				/>
			</Dialog>
		</>
	);
}

interface CombinedAccountFormContentClientProps
	extends Omit<CombinedAccountFormClientProps, "children"> {
	mode: "login" | "create" | "createBusiness";
	setMode: Dispatch<SetStateAction<"login" | "create" | "createBusiness">>;
}

export function CombinedAccountFormContentClient(props: CombinedAccountFormContentClientProps) {
	const { login, create, createBusiness, actions, mode, setMode, variant } = props;

	return match(mode)
		.with("create", () => (
			<>
				{create.form}
				<div className="mt-4 flex flex-col items-start gap-2">
					<Button
						starticon={faUserUnlock}
						color={variant === "light" ? "white" : undefined}
						variant="link"
						onClick={() => setMode("login")}
					>
						{actions.login}
					</Button>
					<Button
						starticon={faUserTie}
						color={variant === "light" ? "white" : undefined}
						variant="link"
						onClick={() => setMode("createBusiness")}
					>
						{actions.createBusiness}
					</Button>
				</div>
			</>
		))
		.with("createBusiness", () => (
			<>
				{createBusiness.form}
				<div className="mt-4 flex flex-col items-start gap-2">
					<Button
						starticon={faUserUnlock}
						color={variant === "light" ? "white" : undefined}
						variant="link"
						onClick={() => setMode("login")}
					>
						{actions.login}
					</Button>
					<Button
						starticon={faUserPlus}
						color={variant === "light" ? "white" : undefined}
						variant="link"
						onClick={() => setMode("create")}
					>
						{actions.create}
					</Button>
				</div>
			</>
		))
		.otherwise(() => (
			<>
				{login.form}
				<div className="mt-4 flex flex-col items-start gap-2">
					<Button
						starticon={faUserPlus}
						color={variant === "light" ? "white" : undefined}
						variant="link"
						onClick={() => setMode("create")}
					>
						{actions.create}
					</Button>
					<Button
						starticon={faUserTie}
						color={variant === "light" ? "white" : undefined}
						variant="link"
						onClick={() => setMode("createBusiness")}
					>
						{actions.createBusiness}
					</Button>
				</div>
			</>
		));
}
